import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "kukkad_cart_v1";
export const DELIVERY_FEE = 39;

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const keyOf = (kind, id) => `${kind}:${id}`;

  const addItem = (product, kind = "menu") => {
    const key = keyOf(kind, product.id);
    setItems((prev) => {
      const existing = prev[key];
      return {
        ...prev,
        [key]: {
          id: product.id,
          kind,
          name: product.name || product.title,
          price: product.price,
          image: product.image || null,
          quantity: (existing?.quantity || 0) + 1,
        },
      };
    });
  };

  const setQuantity = (key, qty) => {
    setItems((prev) => {
      if (qty <= 0) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: { ...prev[key], quantity: qty } };
    });
  };

  const removeItem = (key) =>
    setItems((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });

  const clear = () => setItems({});

  const list = useMemo(() => Object.entries(items).map(([key, v]) => ({ key, ...v })), [items]);
  const count = useMemo(() => list.reduce((s, i) => s + i.quantity, 0), [list]);
  const subtotal = useMemo(() => list.reduce((s, i) => s + i.price * i.quantity, 0), [list]);
  const total = useMemo(() => (count > 0 ? subtotal + DELIVERY_FEE : 0), [subtotal, count]);

  const value = {
    items, list, count, subtotal, total,
    addItem, setQuantity, removeItem, clear,
    open, setOpen,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
